/**
 * 🔴 W130 — THE CLIENT'S WORKOUT, WALKED IN THE COACH'S PHONE PREVIEW (his 2026-10-01 ~22:55 word: *"always have a start
 * workout button and actually the coach see how it will be done in real … most coaches actually never see the client's
 * end"*).
 *
 * A PURE reducer over the door's tree: the pages in the phone pager's order, the ticks, `Mark all`, `Next exercise ›`, the
 * rail, the steps, the END page and the finish page. NOTHING IS SAVED — this module reaches no client, no store, no network
 * and no browser storage (a vitest source ruler and harness gate `w130-the-coach-walks-the-workout` ruler 5 assert it).
 *
 * ⚠️ EVERY RULE HERE IS THE PHONE'S, PINNED, NEVER TYPED FRESH: each function names the Swift it twins, and
 * `playerWalkCorpus.test.ts` holds this module EQUAL to `__fixtures__/playerWalkCorpus.json` — what the phone's own pure rules
 * answered (`tools/engine-tests/PlayerWalkCorpus.swift`) on every step of 40 scripted walks. The constants are the generated
 * `playerFacts.ts`. A rule that cannot be pinned is LEFT OUT of this cut (the rest clock, media, the tools, the hold timer,
 * the clock-run players, the rule chip) rather than guessed.
 *
 * Two departures from the phone, both deliberate and both said: a section the app runs on a CLOCK is ONE page here (the
 * pager would walk its exercises as set tables; the clock player is the next cut), and NOTHING AUTO-ADVANCES (the phone turns
 * the page when the REST ends; `Next exercise ›` is the way on here).
 */
import { durationLabel, kg, runPhrase } from "./phoneDose";
import { distanceText } from "./distanceCell";
import { timeText } from "./typedCell";
import { phoneSectionName } from "./briefing";
import type { AssignedWorkoutTree, TreeExercise, TreeSection, TreeSet } from "./assignedWorkoutTree";
import { PLAYED_FORMATS, RAIL, SPINE, STEPS, WARM_UPS_REST_CEILING } from "./playerFacts";

// ─────────────────────────────────────────── the model (the bridge's mapping) ───────────────────────────────────────────

export type SetKind = "regular" | "warm_up" | "drop_set" | "failure";
const SET_KINDS: readonly SetKind[] = ["regular", "warm_up", "drop_set", "failure"];

export type WalkExercise = {
  id: string; name: string; sets: number; repsLow: number | null; repsHigh: number | null; hold: number | null;
  weight: number | null; rest: number; perReps: number[]; perWeight: number[]; perSecs: number[]; types: SetKind[];
  fields: string[]; note: string; tempo: string; groupId: string | null; exerciseId: string | null;
  /** W136 B2 S3r5 P5 — the typed columns (`CoachSetDose` → `LoadFieldPolicy.splitTime`): a run's / rounds row's time leaves
   *  `hold` / `perSecs` for `time` / `perTime`. */
  perDist: number[]; perRounds: number[]; perTime: number[]; time: number | null;
};
export type WalkSection = {
  name: string; role: string; sectionType: string; format: string; implicit: boolean; exercises: WalkExercise[];
};
export type WalkModel = { workoutName: string; sections: WalkSection[] };

function byPosition<T extends { id: string; position: number | null }>(rows: readonly T[]): T[] {
  return [...rows].sort((a, b) => {
    if (a.position === null && b.position === null) return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
    if (a.position === null) return 1;
    if (b.position === null) return -1;
    if (a.position !== b.position) return a.position - b.position;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });
}

const num = (v: number | string | null | undefined): number | null => {
  if (v === null || v === undefined) return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

/** `EnginePerSet.column`: the values when EVERY row has one, else empty. */
function column<T>(values: readonly (T | null)[]): T[] {
  return values.length === 0 || values.some((v) => v === null) ? [] : (values as T[]);
}

/** `CoachPairing.groups`: runs of `superset_with_next` of two or more; the section's `superset` format a fallback. */
export function pairing(rowIds: readonly string[], withNext: readonly boolean[], sectionIsSuperset: boolean): Map<string, string> {
  const out = new Map<string, string>();
  if (rowIds.length !== withNext.length || rowIds.length === 0) return out;
  let i = 0;
  while (i < rowIds.length) {
    let end = i;
    while (end < rowIds.length - 1 && withNext[end] === true) end += 1;
    if (end - i + 1 >= 2) for (let k = i; k <= end; k += 1) out.set(rowIds[k]!, rowIds[i]!);
    i = end + 1;
  }
  if (out.size === 0 && sectionIsSuperset && rowIds.length >= 2) for (const id of rowIds) out.set(id, rowIds[0]!);
  return out;
}


const FORMATS = ["straight_sets", "superset", "circuit", "emom", "amrap", "timed_interval", "for_time"];

/** The door's tree + each library exercise's `tracking_fields` → what the pages read (`AssignedWorkoutBridge` +
 *  `CoachSetDose` + `TodayViewModel`'s snapshot: rest = the sets' longest ?? 90). */
export function walkModel(tree: AssignedWorkoutTree, trackingFields: ReadonlyMap<string, readonly string[]>): WalkModel {
  return {
    workoutName: tree.name,
    sections: byPosition(tree.coach_workout_sections ?? []).map((s: TreeSection) => {
      const role = s.section_type === "warm_up" || s.section_type === "cool_down" || s.section_type === "recovery" ? s.section_type : "main";
      const format = FORMATS.includes(s.format ?? "") ? (s.format as string) : "straight_sets";
      const rows = byPosition(s.coach_workout_exercises ?? []);
      const groups = pairing(rows.map((r) => r.id), rows.map((r) => r.superset_with_next === true), format === "superset");
      const implicit = s.is_implicit === true;
      return {
        // `W84PlayerPages.bridgeSectionName`, whose web twin already exists (`phoneSectionName`) — one rule, asked here.
        name: phoneSectionName(s.name, format, implicit), role, sectionType: role === "main" ? "workout" : role, format, implicit,
        exercises: rows.map((e: TreeExercise) => exerciseOf(e, groups.get(e.id) ?? null, trackingFields)),
      };
    }),
  };
}

function exerciseOf(e: TreeExercise, groupId: string | null, trackingFields: ReadonlyMap<string, readonly string[]>): WalkExercise {
  const sets: TreeSet[] = byPosition(e.coach_workout_sets ?? []);
  const types = sets.map((s) => s.set_type ?? "regular");
  const working = sets.filter((_, i) => types[i] !== "warm_up");
  const dose = working.length === 0 ? sets : working;
  const nums = (xs: (number | null)[]) => xs.filter((x): x is number => x !== null);
  const reps = nums(dose.map((s) => s.reps)), holds = nums(dose.map((s) => s.time_seconds)), weights = nums(dose.map((s) => num(s.weight_kg)));
  const fields = e.exercise_id === null ? [] : [...(trackingFields.get(e.exercise_id) ?? [])];
  const perDist = column(sets.map((s) => num(s.distance_m ?? null))), perRounds = column(sets.map((s) => s.rounds ?? null));
  const perSecs = column(sets.map((s) => s.time_seconds));
  const typed = isTypedTimeRow(fields, perDist.length > 0, perRounds.length > 0);
  const min = (xs: number[]) => (xs.length === 0 ? null : Math.min(...xs));
  const max = (xs: number[]) => (xs.length === 0 ? null : Math.max(...xs));
  const rests = nums(sets.map((s) => s.rest_seconds));
  const note = [e.each_side === true ? "Each side." : null, e.hold_until_lost === true ? "Until lost." : null, (e.notes ?? "").trim() || null].filter((x): x is string => x !== null).join(" ");
  return {
    id: e.id, name: e.coach_exercises?.name ?? "", sets: Math.max(sets.length, 1), repsLow: min(reps), repsHigh: max(reps),
    hold: typed ? null : min(holds), weight: max(weights), rest: max(rests) ?? 90,
    perReps: column(sets.map((s) => s.reps)), perWeight: column(sets.map((s) => num(s.weight_kg))),
    perSecs: typed ? [] : perSecs,
    types: types.map((t) => (SET_KINDS.includes(t as SetKind) ? (t as SetKind) : "regular")),
    fields,
    note, tempo: e.tempo ?? "", groupId, exerciseId: e.exercise_id,
    perDist, perRounds, perTime: typed ? perSecs : [], time: typed ? min(holds) : null,
  };
}

const setKind = (e: WalkExercise, i: number): SetKind => e.types[i] ?? "regular";

// ─────────────────────────────────────────── SupersetRounds / SkippedByOmission ────────────────────────────────────────────

export type Slot = { member: number; setIndex: number };
export type Round = { slots: Slot[]; isWarmUp: boolean; ordinal: number };
const sameSlot = (a: Slot | null | undefined, b: Slot | null | undefined) =>
  a != null && b != null && a.member === b.member && a.setIndex === b.setIndex;

/** `SupersetRounds.leadingCount`. */
export function leadingCount(types: readonly boolean[]): number {
  let n = 0;
  for (const t of types) { if (t) n += 1; else break; }
  return n;
}

/** `SupersetRounds.plan`. */
export function plan(warmUps: readonly (readonly boolean[])[]): Round[] {
  const leading = warmUps.map(leadingCount);
  const rounds: Round[] = [];
  const warmRounds = Math.max(0, ...leading);
  for (let w = 0; w < warmRounds; w += 1) {
    rounds.push({ slots: leading.flatMap((l, m) => (l > w ? [{ member: m, setIndex: w }] : [])), isWarmUp: true, ordinal: w + 1 });
  }
  const working = warmUps.map((w, m) => w.length - leading[m]!);
  const workRounds = Math.max(0, ...working);
  for (let r = 0; r < workRounds; r += 1) {
    rounds.push({ slots: working.flatMap((n, m) => (n > r ? [{ member: m, setIndex: leading[m]! + r }] : [])), isWarmUp: false, ordinal: r + 1 });
  }
  return rounds;
}

/** `SkippedByOmission.forward`. */
export function forward(last: number | null, count: number, isLogged: (i: number) => boolean): number | null {
  const start = last === null ? 0 : last + 1;
  for (let i = start; i < Math.max(start, count); i += 1) if (!isLogged(i)) return i;
  return null;
}

/** `SkippedByOmission.liveSet`. */
export function liveSet(logged: ReadonlySet<number>, count: number): number {
  const last = logged.size === 0 ? null : Math.max(...logged);
  return forward(last, count, (i) => logged.has(i)) ?? count;
}

/** `SupersetRounds.forward`. */
export function forwardSlot(last: Slot | null, rounds: readonly Round[], isLogged: (s: Slot) => boolean): Slot | null {
  const flat = rounds.flatMap((r) => r.slots);
  const at = last === null ? -1 : flat.findIndex((s) => sameSlot(s, last));
  const i = forward(at < 0 ? null : at, flat.length, (k) => isLogged(flat[k]!));
  return i === null ? null : flat[i]!;
}

/** `SupersetRounds.liveMember`. */
export function liveMember(sets: readonly number[], isLogged: (s: Slot) => boolean): number | null {
  const rounds = plan(sets.map((n) => Array<boolean>(Math.max(n, 0)).fill(false)));
  const flat = rounds.flatMap((r) => r.slots);
  let last: Slot | null = null;
  for (const s of flat) if (isLogged(s)) last = s;
  return forwardSlot(last, rounds, isLogged)?.member ?? null;
}

export type Fill = "all" | "some" | "none";
/** `SupersetRounds.fill`. */
export function fill(round: Round, isLogged: (s: Slot) => boolean): Fill {
  const n = round.slots.filter(isLogged).length;
  return n === 0 ? "none" : n === round.slots.length ? "all" : "some";
}

/** `SupersetRounds.restAfter` — zero after a member a partner follows in the round, the round's longest rest after the last. */
export function restAfter(slot: Slot, rounds: readonly Round[], memberRest: readonly number[]): number {
  const round = rounds.find((r) => r.slots.some((s) => sameSlot(s, slot)));
  if (round === undefined) return 0;
  const flat = rounds.flatMap((r) => r.slots);
  const i = flat.findIndex((s) => sameSlot(s, slot));
  const following = i >= 0 && i + 1 < flat.length ? flat[i + 1] : undefined;
  if (following !== undefined && round.slots.some((s) => sameSlot(s, following))) return 0;
  return round.slots.length === 0 ? 0 : Math.max(...round.slots.map((s) => memberRest[s.member] ?? 0));
}

// ─────────────────────────────────────────── the small rules a row reads ────────────────────────────────────────────

/** `SetCarryForward.targets` (enabled: its storage default, ON). */
export function carryTargets(index: number, kinds: readonly SetKind[], logged: ReadonlySet<number>): number[] {
  if (index < 0 || index >= kinds.length || kinds[index] === "warm_up") return [];
  return kinds.flatMap((k, j) => (j > index && k !== "warm_up" && !logged.has(j) ? [j] : []));
}

/** `EnginePerSet.seeded`. */
export function seeded<T>(perSet: readonly T[], count: number, fallback: T): T[] {
  return Array.from({ length: Math.max(0, count) }, (_, i) => (i < perSet.length ? perSet[i]! : fallback));
}

/** `SetType.label(at:in:)`: W or D, or the WORKING rank (warm-ups uncounted) — a set to failure is NUMBERED (his 5 Oct note). */
export function setLabel(i: number, types: readonly SetKind[]): string {
  if (i < 0 || i >= types.length) return String(i + 1);
  const letter = { regular: null, warm_up: "W", drop_set: "D", failure: null }[types[i]!];
  if (letter !== null) return letter;
  return String(types.slice(0, i + 1).filter((t) => t !== "warm_up").length);
}

/** `SetRestPlan.seconds(afterSet:kinds:exerciseRest:)`. */
export function restAfterSet(i: number, kinds: readonly SetKind[], exerciseRest: number): number {
  if (kinds[i] !== "warm_up") return exerciseRest;
  if (kinds[i + 1] !== "warm_up") return exerciseRest;
  return Math.min(exerciseRest, WARM_UPS_REST_CEILING);
}

/** `SetRestText.label`. */
export const restText = (seconds: number): string => (seconds <= 0 ? "none" : durationLabel(seconds));

/** `LoadFieldPolicy.setIsTimed` (W136 B1) — is THIS SET timed: a prescribed time wins over the exercise's fields. */
export const setIsTimed = (prescribedHoldSeconds: number | null): boolean => prescribedHoldSeconds !== null;

/** `LoadFieldPolicy.showsLoad` (no history in a preview). */
export function showsLoad(weight: number | null, fields: readonly string[], sectionType: string): boolean {
  if (weight !== null) return true;
  if (fields.length > 0) return fields.includes("weight");
  return !["warm_up", "cool_down", "mobility"].includes(sectionType);
}
/** 🔴 W136 B2 S3r5 P5 — `LoadFieldPolicy.SetColumn` / `setColumns` / `isTypedTimeRow` / `loggedValues`: the phone's ONE column
 *  rule (design §3), held to the Swift-emitted walk corpus. */
export type SetColumn = "kg" | "reps" | "hold" | "typedTime" | "rounds" | "m" | "km";
export const COLUMN_HEADER: Readonly<Record<SetColumn, string>> = {
  kg: "KG", reps: "REPS", hold: "TIME", typedTime: "TIME", rounds: "ROUNDS", m: "M", km: "KM",
};
const DISTANCE_FIELDS = ["distance_m", "distance_km"];
export function isTypedTimeRow(fields: readonly string[], prescribesDistance: boolean, prescribesRounds: boolean): boolean {
  return prescribesDistance || prescribesRounds || fields.some((f) => DISTANCE_FIELDS.includes(f) || f === "rounds");
}
export function setColumns(fields: readonly string[], load: boolean, setTimed: boolean, setReps: boolean,
                           setDistance: boolean, setRounds: boolean): SetColumn[] {
  if (fields.length === 0 && !setDistance && !setRounds) {
    const count: SetColumn = setTimed ? "hold" : "reps";
    return load ? [count, "kg"] : [count];
  }
  const timeColumn: SetColumn = isTypedTimeRow(fields, setDistance, setRounds) ? "typedTime" : "hold";
  let count: { column: SetColumn; word: string } | null;
  if (setTimed) count = { column: timeColumn, word: fields.includes("time") ? "time" : "reps" };
  else if (fields.includes("reps") || setReps) count = { column: "reps", word: "reps" };
  else if (fields.includes("time")) count = { column: timeColumn, word: "time" };
  else count = null;
  const distanceWord = fields.find((f) => DISTANCE_FIELDS.includes(f)) ?? null;
  const at = (f: string): number | null => { const i = fields.indexOf(f); return i < 0 ? null : i; };
  const keyed: [number, SetColumn][] = [];
  const countKey = count === null ? null : at(count.word) ?? -1;
  if (count !== null && countKey !== null) keyed.push([countKey, count.column]);
  if (load) keyed.push([at("weight") ?? (countKey ?? -1) + 0.5, "kg"]);
  if (distanceWord !== null || setDistance) keyed.push([(distanceWord === null ? null : at(distanceWord)) ?? 100, distanceWord === "distance_km" ? "km" : "m"]);
  if (fields.includes("rounds") || setRounds) keyed.push([at("rounds") ?? 101, "rounds"]);
  return keyed.sort((a, b) => a[0] - b[0]).map((k) => k[1]);
}
export type LoggedValues = { weightKg: number | null; reps: number | null; holdSeconds: number | null; distanceM: number | null; rounds: number | null };
export function loggedValues(columns: readonly SetColumn[], v: { load: number | null; reps: number | null; hold: number | null;
                             typed: number | null; dist: number | null; rounds: number | null }): LoggedValues {
  return {
    weightKg: columns.includes("kg") ? v.load : null,
    reps: columns.includes("reps") ? v.reps : null,
    holdSeconds: columns.includes("hold") ? v.hold : columns.includes("typedTime") ? v.typed : null,
    distanceM: columns.includes("m") || columns.includes("km") ? v.dist : null,
    rounds: columns.includes("rounds") ? v.rounds : null,
  };
}

/** `LoadFieldPolicy.loadLeads`. */
export function loadLeads(fields: readonly string[], countField: string): boolean {
  const l = fields.indexOf("weight"), c = fields.indexOf(countField);
  return l >= 0 && c >= 0 && l < c;
}

/** `CoachNoteSplit.onPage`: the first paragraph within 200 characters, cut at a sentence (never inside the first 40) or a
 *  word, with ` …` when anything was withheld. */
export function noteOnPage(note: string): string {
  const trimmed = note.trim();
  const sep = trimmed.indexOf("\n\n");
  const para = (sep >= 0 ? trimmed.slice(0, sep) : trimmed).trim();
  let shown: string;
  if (para.length <= 200) shown = para;
  else {
    const chars = [...para];
    let boundary = 0;
    for (let i = 0; i < Math.min(chars.length - 1, 200); i += 1) {
      if (".!?".includes(chars[i]!) && (chars[i + 1] === " " || chars[i + 1] === "\n") && i + 1 >= 40) boundary = i + 1;
    }
    if (boundary > 0) shown = chars.slice(0, boundary).join("").trim();
    else {
      let cut = 200;
      while (cut > 0 && chars[cut] !== " ") cut -= 1;
      if (cut === 0) cut = 200;
      shown = chars.slice(0, cut).join("").trim();
    }
  }
  return shown !== trimmed && shown !== "" ? `${shown} …` : shown;
}

/** `SessionReadout.line` — `AssistLoad.prescriptionLine` over the performed sets' own columns (no ranges, no types). */
export function readoutLine(sets: readonly { setNumber: number; reps: number | null; load: number | null; secs: number | null;
                                             dist?: number | null; rounds?: number | null }[], distanceKm = false): string {
  const ordered = [...sets].sort((a, b) => a.setNumber - b.setNumber);
  if (ordered.length === 0) return "";
  // W136 B2 S3r5 P5 — a run / rounds day in §5's words (`runPhrase`); its time is typed, never the hold head.
  const typed = ordered.some((s) => (s.dist ?? null) !== null || (s.rounds ?? null) !== null);
  const reps = column(ordered.map((s) => s.reps)), secs = typed ? [] : column(ordered.map((s) => s.secs)), weights = column(ordered.map((s) => s.load));
  const run = typed ? runPhrase(ordered.length, column(ordered.map((s) => s.dist ?? null)), column(ordered.map((s) => s.rounds ?? null)),
                                reps, column(ordered.map((s) => s.secs)), distanceKm) : null;
  const spoken = <T,>(values: T[], unit: string, format: (v: T) => string): string | null => {
    const first = values[0];
    if (first === undefined) return null;
    if (values.every((v) => v === first)) return `${String(values.length)} × ${format(first)}${unit}`;
    return values.map(format).join("/") + unit;
  };
  const head = run ?? spoken(reps, " reps", (v) => String(v)) ?? spoken(secs, "", durationLabel)
    ?? (ordered.length === 1 ? "1 set" : `${String(ordered.length)} sets`);
  const firstW = weights[0];
  let load: string | null;
  if (firstW !== undefined && !weights.every((w) => w === firstW)) load = `@ ${weights.map(kg).join("/")} kg`;
  else load = firstW !== undefined && firstW > 0 ? `@ ${kg(firstW)} kg` : null;
  return load === null ? head : `${head} ${load}`;
}

// ─────────────────────────────────────────── W84PlayerPages: the pages ────────────────────────────────────────────

export type PageKind =
  | { kind: "exercise"; exercise: number }
  | { kind: "rungs"; exercise: number; count: number }
  | { kind: "round"; members: number[]; round: number; roundCount: number; slots: Slot[]; groupKey: string }
  | { kind: "timed" };
export type Page = PageKind & { section: number; key: string };

type Member = { groupId: string | null; sets: number; warmUps: boolean[] };

/** `W84PlayerPages.sectionPages`: groups gathered where their first member stands — rung pages, then the working rounds. */
export function sectionPages(members: readonly Member[], section: number): Page[] {
  const order: { gid: string | null; index: number }[] = [];
  const groups = new Map<string, number[]>();
  members.forEach((m, i) => {
    if (m.groupId !== null) {
      if (!groups.has(m.groupId)) order.push({ gid: m.groupId, index: i });
      groups.set(m.groupId, [...(groups.get(m.groupId) ?? []), i]);
    } else order.push({ gid: null, index: i });
  });
  const out: Page[] = [];
  for (const o of order) {
    const idxs = o.gid === null ? undefined : groups.get(o.gid);
    if (o.gid === null || idxs === undefined || idxs.length === 0) {
      out.push({ kind: "exercise", exercise: o.index, section, key: `e-${String(section)}-${String(o.index)}` });
      continue;
    }
    const lead = idxs.map((i) => leadingCount(members[i]!.warmUps));
    const p = plan(idxs.map((i, k) => {
      const n = Math.max(members[i]!.sets, 1);
      const l = Math.min(lead[k]!, n);
      return [...Array<boolean>(l).fill(true), ...Array<boolean>(n - l).fill(false)];
    })).filter((r) => !r.isWarmUp);
    idxs.forEach((i, k) => {
      const n = Math.min(lead[k]!, members[i]!.sets);
      if (n > 0) out.push({ kind: "rungs", exercise: i, count: n, section, key: `r-${String(section)}-${String(i)}` });
    });
    const count = Math.max(p.length, 1);
    /* The phone's key is `group-` + the group's UUID as Swift spells it (`uuidString`, UPPERCASE) — only its identity is
       read, but the corpus compares the page list, keys included. */
    const groupKey = `group-${o.gid.toUpperCase()}`;
    for (let r = 0; r < count; r += 1) {
      out.push({ kind: "round", members: idxs, round: r, roundCount: count, slots: p[r]?.slots ?? [], groupKey, section, key: groupKey });
    }
  }
  return out;
}

/** The web's pages: the pager's, with a section the app runs on a clock collapsed to ONE page (`SectionSchedule.isPlayed`). */
export function pages(model: WalkModel): Page[] {
  const out: Page[] = [];
  model.sections.forEach((s, si) => {
    const ps = sectionPages(s.exercises.map((e) => ({
      groupId: e.groupId, sets: e.sets,
      warmUps: Array.from({ length: Math.max(e.sets, 1) }, (_, i) => setKind(e, i) === "warm_up"),
    })), si);
    if (PLAYED_FORMATS.includes(s.format)) {
      if (ps.length > 0) out.push({ kind: "timed", section: si, key: `t-${String(si)}` });
      return;
    }
    out.push(...ps);
  });
  return out;
}

/** `W84PlayerPages.nextExercisePage`. */
export function nextExercisePage(keys: readonly string[], from: number): number {
  if (from < 0 || from >= keys.length) return keys.length;
  for (let i = from + 1; i < keys.length; i += 1) if (keys[i] !== keys[from]) return i;
  return keys.length;
}

/** `W84PlayerPages.memberOnScreen`. */
export function memberOnScreen(sets: readonly number[], slots: readonly Slot[], isLogged: (s: Slot) => boolean): number {
  const m = liveMember(sets, isLogged);
  if (m !== null && slots.some((s) => s.member === m)) return m;
  return slots.find((s) => !isLogged(s))?.member ?? slots[0]?.member ?? 0;
}

/** `W84PlayerPages.focusSlot`. */
export function focusSlot(roundSlots: readonly Slot[], rounds: readonly Round[], lastLogged: Slot | null, isLogged: (s: Slot) => boolean): Slot | null {
  const n = forwardSlot(lastLogged, rounds, isLogged);
  if (n !== null && roundSlots.some((s) => sameSlot(s, n))) return n;
  return roundSlots.find((s) => !isLogged(s)) ?? null;
}

/** `W84PlayerPages.markAll`. */
export function markAllPlan(setCount: number, logged: ReadonlySet<number>): { unmark: boolean; sets: number[] } {
  if (logged.size >= setCount) return { unmark: true, sets: [...logged].sort((a, b) => a - b) };
  return { unmark: false, sets: Array.from({ length: Math.max(setCount, 0) }, (_, i) => i).filter((i) => !logged.has(i)) };
}

// ─────────────────────────────────────────── the rail and the steps ────────────────────────────────────────────

export type RailKind = "warmUp" | "coolDown" | "standalone" | "supersetFirst" | "supersetMiddle" | "supersetLast";
export type RailEntry = { kind: RailKind; section: number };

/** `SpineConnector.run`. */
export function connectors(groupIds: readonly (string | null)[]): RailKind[] {
  const out: RailKind[] = [];
  let i = 0;
  while (i < groupIds.length) {
    const g = groupIds[i];
    if (g === null || g === undefined) { out.push("standalone"); i += 1; continue; }
    let j = i + 1;
    while (j < groupIds.length && groupIds[j] === g) j += 1;
    const count = j - i;
    for (let k = 0; k < count; k += 1) out.push(count === 1 ? "standalone" : k === 0 ? "supersetFirst" : k === count - 1 ? "supersetLast" : "supersetMiddle");
    i = j;
  }
  return out;
}

/** `W84PlayerRail.entries`. */
export function railEntries(model: WalkModel): RailEntry[] {
  return model.sections.flatMap((s, si): RailEntry[] => {
    if (s.role === "warm_up") return [{ kind: "warmUp", section: si }];
    if (s.role === "cool_down") return [{ kind: "coolDown", section: si }];
    return connectors(s.exercises.map((e) => e.groupId)).map((kind) => ({ kind, section: si }));
  });
}

/** `W84PlayerRail.entryIndex`. */
export function entryIndex(entries: readonly RailEntry[], section: number, exerciseIndex: number): number | null {
  const mine = entries.flatMap((e, i) => (e.section === section ? [i] : []));
  const first = mine[0];
  if (first === undefined) return null;
  if (entries[first]!.kind === "warmUp" || entries[first]!.kind === "coolDown") return first;
  return mine[exerciseIndex] ?? null;
}

export const isGlyph = (e: RailEntry) => e.kind === "warmUp" || e.kind === "coolDown";

export type RailLayout = { xs: number[]; pitch: number; regionLo: number; regionHi: number; windowed: boolean };

/** `W84PlayerRail.layout`. */
export function railLayout(entries: readonly RailEntry[], width: number, current: number | null): RailLayout {
  const n = entries.length, inset = RAIL.inset;
  if (n === 0) return { xs: [], pitch: 0, regionLo: 0, regionHi: width, windowed: false };
  let lead = 0;
  while (lead < n && isGlyph(entries[lead]!)) lead += 1;
  let trail = 0;
  while (trail < n - lead && isGlyph(entries[n - 1 - trail]!)) trail += 1;
  // His 2026-10-05 word: the gap beside a pinned glyph is one pitch PLUS the cup's reach past a dot's (`glyphGapExtra`).
  const from = lead > 0 ? RAIL.glyphZone / 2 + (lead - 1) * RAIL.glyphZone + RAIL.glyphGapExtra : inset;
  const to = trail > 0 ? width - RAIL.glyphZone / 2 - (trail - 1) * RAIL.glyphZone - RAIL.glyphGapExtra : width - inset;
  const edges = (lead > 0 ? 1 : 0) + (trail > 0 ? 1 : 0);
  const region = (pitch: number) => ({ lo: from + (lead > 0 ? pitch : 0), hi: to - (trail > 0 ? pitch : 0) });
  const mid: number[] = [];
  for (let i = lead; i < n - trail; i += 1) mid.push(i);
  const xs = Array<number>(n).fill(0);
  for (let k = 0; k < lead; k += 1) xs[k] = RAIL.glyphZone / 2 + k * RAIL.glyphZone;
  for (let k = 0; k < trail; k += 1) xs[n - 1 - k] = width - RAIL.glyphZone / 2 - k * RAIL.glyphZone;
  if (mid.length === 0) return { xs, pitch: 0, regionLo: from, regionHi: to, windowed: false };
  if (mid.length === 1) { xs[mid[0]!] = (from + to) / 2; return { xs, pitch: 0, regionLo: from, regionHi: to, windowed: false }; }
  const group = mid.slice(0, -1).map((e) => entries[e]!.kind === "supersetFirst" || entries[e]!.kind === "supersetMiddle");
  const k = group.filter((g) => g).length, m = group.length - k;
  const place = (pitch: number, shift: number) => {
    let x = region(pitch).lo - shift;
    mid.forEach((e, i) => {
      xs[e] = x;
      if (i < group.length) x += group[i] ? Math.max(pitch, RAIL.groupPitch) : pitch;
    });
  };
  const equal = (to - from) / (k + m + edges);
  if (equal >= RAIL.groupPitch || (k === 0 && equal >= RAIL.minPitch)) {
    place(equal, 0);
    return { xs, pitch: equal, regionLo: region(equal).lo, regionHi: region(equal).hi, windowed: false };
  }
  if (m + edges > 0) {
    const rest = (to - from - k * RAIL.groupPitch) / (m + edges);
    if (rest >= RAIL.minPitch) { place(rest, 0); return { xs, pitch: rest, regionLo: region(rest).lo, regionHi: region(rest).hi, windowed: false }; }
  }
  const { lo, hi } = region(RAIL.minPitch);
  const span = hi - lo;
  place(RAIL.minPitch, 0);
  const first = xs[mid[0]!]!;
  const total = xs[mid[mid.length - 1]!]! - first;
  const cur = current === null ? 0 : current < lead ? 0 : current >= n - trail ? total : xs[current]! - first;
  const shift = Math.max(Math.min(cur - span / 2, total - span), 0);
  place(RAIL.minPitch, shift);
  return { xs, pitch: RAIL.minPitch, regionLo: lo, regionHi: hi, windowed: true };
}

export type SegmentEnds = { drawFrom: number; drawTo: number };
/** `SpineLine.ends` — W141: a glyph end stops at the cup (`cupRadius` from the glyph's centre), at full ink; the cup carries
 *  the fade now. */
export function segmentEnds(length: number, glyphAtStart: boolean, glyphAtEnd: boolean): SegmentEnds | null {
  const from = glyphAtStart ? SPINE.cupRadius : 0;
  const to = glyphAtEnd ? length - SPINE.cupRadius : length;
  if (!(to > from)) return null;
  return { drawFrom: from, drawTo: to };
}
/** `SpineLine.cupArc` — the cup's arc round a glyph in degrees (0 = +x, clockwise with y down), `facing` its neighbour. */
export const cupArc = (facing: number) => ({ from: facing - 90 - SPINE.cupOverrun, to: facing + 90 + SPINE.cupOverrun });

/** `W84PlayerRail.curve` (W141) — ONE quadratic from the dot at `a1` to the dot at `a2`, peaking at `height` midway, the
 *  arrowhead at `a2` along the end tangent; points are (along, across), across measured away from the line on its side. */
export type AC = readonly [number, number];
export type RailCurve = { side: 1 | -1; start: AC; control: AC; strokeEnd: AC; tip: AC; left: AC; right: AC; peak: number; extent: number };
export function railCurve(a1: number, a2: number, height: number, side: 1 | -1): RailCurve {
  const c0 = RAIL.curveFoot;
  const start: AC = [a1, c0], end: AC = [a2, c0];
  const control: AC = [(a1 + a2) / 2, 2 * height - c0];
  const q = (t: number): AC => {
    const u = 1 - t;
    return [u * u * start[0] + 2 * u * t * control[0] + t * t * end[0], u * u * start[1] + 2 * u * t * control[1] + t * t * end[1]];
  };
  const ta = end[0] - control[0], tc = end[1] - control[1];
  const len = Math.max(Math.sqrt(ta * ta + tc * tc), 1e-9);
  const ua = ta / len, uc = tc / len;
  const base: AC = [end[0] - RAIL.arrowLength * ua, end[1] - RAIL.arrowLength * uc];
  const left: AC = [base[0] - RAIL.arrowHalfWidth * uc, base[1] + RAIL.arrowHalfWidth * ua];
  const right: AC = [base[0] + RAIL.arrowHalfWidth * uc, base[1] - RAIL.arrowHalfWidth * ua];
  let lo = 0, hi = 1;
  for (let k = 0; k < 40; k += 1) {
    const mid = (lo + hi) / 2, p = q(mid);
    const d = Math.sqrt((p[0] - end[0]) * (p[0] - end[0]) + (p[1] - end[1]) * (p[1] - end[1]));
    if (d > (RAIL.arrowLength * 2) / 3) lo = mid; else hi = mid;
  }
  const t = lo;
  const splitControl: AC = [start[0] + t * (control[0] - start[0]), start[1] + t * (control[1] - start[1])];
  return { side, start, control: splitControl, strokeEnd: q(t), tip: end, left, right, peak: height, extent: Math.max(height, left[1], right[1]) };
}
/** `SpineTrack.drawArcs(style: .curve)` — per superset run first…last: forward over the line, then back under it. */
export function railCurves(entries: readonly RailEntry[], xs: readonly number[]): RailCurve[] {
  const out: RailCurve[] = [];
  let runStart: number | null = null;
  entries.forEach((e, i) => {
    if (e.kind === "supersetFirst") runStart = i;
    else if (e.kind === "supersetLast") {
      if (runStart !== null && xs[runStart] !== undefined && xs[i] !== undefined) {
        out.push(railCurve(xs[runStart]!, xs[i]!, RAIL.curveForward, 1), railCurve(xs[i]!, xs[runStart]!, RAIL.curveBack, -1));
      }
      runStart = null;
    }
  });
  return out;
}
/** `SpineLine.isSolid`. */
export const isSolid = (i: number, solidThrough: number | null) => solidThrough !== null && i + 1 <= solidThrough;
/** `SpineLine.dashedAhead` (W134's exemption). */
export function dashedAhead(e: SegmentEnds, glyphAtStart: boolean, glyphAtEnd: boolean): boolean {
  if (!glyphAtStart && !glyphAtEnd) return true;
  const visible = (e.drawTo - e.drawFrom) - (glyphAtStart ? 0 : SPINE.dotRadius) - (glyphAtEnd ? 0 : SPINE.dotRadius);
  return visible >= SPINE.legibleDashRun;
}
/** `SpineLine.dashPhase` (Swift's `truncatingRemainder`, then made positive). */
export function dashPhase(e: SegmentEnds, glyphAtStart: boolean, glyphAtEnd: boolean, travelled: number): number {
  const period = 2 * SPINE.dash;
  const mod = (x: number) => { const r = x % period; return r < 0 ? r + period : r; };
  if (glyphAtEnd) return mod(SPINE.dash - (e.drawTo - e.drawFrom));
  if (glyphAtStart) return 0;
  return mod(travelled + e.drawFrom);
}

export type Segment = { drawn: false } | ({ drawn: true; style: "solid" | "dashed" | "continuous"; phase: number } & SegmentEnds);

export function segments(entries: readonly RailEntry[], xs: readonly number[], current: number | null): Segment[] {
  const solidThrough = current ?? (entries.length === 0 ? null : entries.length - 1);
  const out: Segment[] = [];
  let travelled = 0;
  for (let i = 0; i < Math.max(entries.length - 1, 0); i += 1) {
    const length = xs[i + 1]! - xs[i]!;
    const gs = isGlyph(entries[i]!), ge = isGlyph(entries[i + 1]!);
    const e = length > 0 ? segmentEnds(length, gs, ge) : null;
    if (e === null) out.push({ drawn: false });
    else {
      const solid = isSolid(i, solidThrough);
      const dashed = !solid && dashedAhead(e, gs, ge);
      out.push({ drawn: true, ...e, style: solid ? "solid" : dashed ? "dashed" : "continuous", phase: dashed ? dashPhase(e, gs, ge, travelled) : 0 });
    }
    travelled += length;
  }
  return out;
}

/** `W84Steps.label` / `title` / `titleOverSteps` (W141: only `SUPERSET` rides over its steps — a warm-up's / cool-down's
 *  title is the back row's, `headTitle`). */
export const stepLabel = (kind: "rounds" | "warmUp" | "coolDown", i: number) =>
  kind === "rounds" ? String(i + 1) : `${kind === "warmUp" ? "W" : "C"}${String(i + 1)}`;
export const stepTitle = (kind: "rounds" | "warmUp" | "coolDown") =>
  kind === "rounds" ? "SUPERSET" : kind === "warmUp" ? "WARM-UP" : "COOL-DOWN";
export const titleOverSteps = (kind: "rounds" | "warmUp" | "coolDown") => (kind === "rounds" ? stepTitle(kind) : null);
/** `W84Steps.exerciseFill`. */
export function exerciseFill(sets: number, loggedSetNumbers: ReadonlySet<number>): Fill {
  const n = Math.max(sets, 1);
  let done = 0;
  for (let k = 1; k <= n; k += 1) if (loggedSetNumbers.has(k)) done += 1;
  return done === 0 ? "none" : done === n ? "all" : "some";
}
/** `W84Steps.layout`. */
export function stepsLayout(count: number, width: number, maxSlot: number): { slot: number; gap: number; scrolls: boolean } {
  const laidOut = Math.max(Math.min(count, STEPS.maxEven), 1);
  const slot = Math.min(maxSlot, width / laidOut);
  const gap = laidOut > 1 ? Math.min(28, Math.max(0, (width - laidOut * slot) / (laidOut - 1))) : 0;
  return { slot, gap, scrolls: count > STEPS.maxEven };
}

/** `W84PlayerHead.positionTail` / `headTitle` / `spoken`. */
export function positionTail(isEnd: boolean, index: number, count: number): string {
  return !isEnd && count > 1 && index >= 0 && index < count ? ` · exercise ${String(index + 1)} of ${String(count)}` : "";
}
/** W141: `WARM-UP` / `COOL-DOWN` in the back row on those pages (no position tail), nil elsewhere. */
export const headTitle = (role: string | null, isEnd: boolean): string | null =>
  isEnd ? null : role === "warm_up" ? stepTitle("warmUp") : role === "cool_down" ? stepTitle("coolDown") : null;
export function spokenHead(isEnd: boolean, section: number, sections: number, index: number, count: number): string {
  if (isEnd) return `All ${String(sections)} sections done`;
  /* What VoiceOver says for the head — a POSITION, never a section's name (the phone names a section `MAIN`/`Superset`;
     `partWording.test.ts` forbids a generated `Section N` NAME, and this is not one). Built by concatenation for that
     reason, and pinned to `W84PlayerHead.spoken` by the corpus. */
  const head = "Section " + String(section + 1) + " of " + String(sections);
  return count > 0 ? `${head}, exercise ${String(index + 1)} of ${String(count)}` : head;
}

// ─────────────────────────────────────────── the walk: state + reducer ────────────────────────────────────────────

export type Logged = { reps: number | null; load: number | null; secs: number | null; dist?: number | null; rounds?: number | null };
export type WalkState = {
  page: number;
  /** The store: per exercise row, set index → the numbers the tick logged. */
  logs: Record<string, Record<number, Logged>>;
  /** `exerciseStatus == completed`. */
  completed: string[];
  /** Page-scoped numbers, keyed `<page or group key>|<row id>` (a page's arrays; a superset's shared model). */
  reps: Record<string, number[]>;
  secs: Record<string, number[]>;
  load: Record<string, (number | null)[]>;
  /** W136 B2 S3r5 P5 — the typed cells (none prescribed → null, never a literal). */
  dist: Record<string, (number | null)[]>;
  rounds: Record<string, (number | null)[]>;
  time: Record<string, (number | null)[]>;
  /** An exercise page's `currentSetIndex`. */
  live: Record<string, number>;
  /** A superset's `lastLogged`. */
  lastLogged: Record<string, Slot | null>;
};

export type Walk = { model: WalkModel; pages: Page[] };
export function walk(model: WalkModel): Walk { return { model, pages: pages(model) }; }

const ex = (w: Walk, s: number, e: number) => w.model.sections[s]!.exercises[e]!;
const isLoggedIn = (st: WalkState, id: string, i: number) => st.logs[id]?.[i] !== undefined;
const loggedSet = (st: WalkState, id: string) => new Set(Object.keys(st.logs[id] ?? {}).map(Number));

function pageSetCount(w: Walk, p: Page): number | null {
  if (p.kind === "exercise") return ex(w, p.section, p.exercise).sets;
  if (p.kind === "rungs") return p.count;
  return null;
}
/** A page's own `completedSets` — a rungs page sees only the rungs it owns. */
function pageCompleted(w: Walk, st: WalkState, p: Page & { exercise: number }): Set<number> {
  const all = loggedSet(st, ex(w, p.section, p.exercise).id);
  return p.kind === "rungs" ? new Set([...all].filter((i) => i < p.count)) : all;
}
const groupPlan = (w: Walk, s: number, members: readonly number[]) => plan(members.map((m) => {
  const e = ex(w, s, m);
  return Array.from({ length: Math.max(e.sets, 1) }, (_, i) => setKind(e, i) === "warm_up");
})).filter((r) => !r.isWarmUp);

/** The state a walk starts in: page 0, nothing logged, every page's numbers seeded from the prescription. */
export function initialState(w: Walk): WalkState {
  const st: WalkState = { page: 0, logs: {}, completed: [], reps: {}, secs: {}, load: {}, dist: {}, rounds: {}, time: {}, live: {}, lastLogged: {} };
  const seed = (scope: string, e: WalkExercise, count: number) => {
    const k = `${scope}|${e.id}`;
    if (st.reps[k] !== undefined) return;
    st.reps[k] = seeded(e.perReps, count, e.repsHigh ?? e.repsLow ?? 8);
    st.load[k] = seeded<number | null>(e.perWeight, count, e.weight);
    st.secs[k] = seeded(e.perSecs, count, e.hold ?? 30);
    st.dist[k] = seeded<number | null>(e.perDist, count, null);
    st.rounds[k] = seeded<number | null>(e.perRounds, count, null);
    st.time[k] = seeded<number | null>(e.perTime, count, e.time);
  };
  for (const p of w.pages) {
    if (p.kind === "exercise" || p.kind === "rungs") seed(p.key, ex(w, p.section, p.exercise), pageSetCount(w, p)!);
    else if (p.kind === "round") {
      const rounds = Math.max(Math.max(1, ...p.members.map((m) => ex(w, p.section, m).sets)), 1);
      for (const m of p.members) seed(p.groupKey, ex(w, p.section, m), Math.max(ex(w, p.section, m).sets, rounds, 1));
    }
  }
  return st;
}

function clone(st: WalkState): WalkState {
  return {
    page: st.page, logs: Object.fromEntries(Object.entries(st.logs).map(([k, v]) => [k, { ...v }])), completed: [...st.completed],
    reps: Object.fromEntries(Object.entries(st.reps).map(([k, v]) => [k, [...v]])),
    secs: Object.fromEntries(Object.entries(st.secs).map(([k, v]) => [k, [...v]])),
    load: Object.fromEntries(Object.entries(st.load).map(([k, v]) => [k, [...v]])),
    dist: Object.fromEntries(Object.entries(st.dist).map(([k, v]) => [k, [...v]])),
    rounds: Object.fromEntries(Object.entries(st.rounds).map(([k, v]) => [k, [...v]])),
    time: Object.fromEntries(Object.entries(st.time).map(([k, v]) => [k, [...v]])),
    live: { ...st.live }, lastLogged: { ...st.lastLogged },
  };
}

/** The table's value columns for this row — `vm.setColumns` (the phone's one wire into the rule). */
export function columnsOf(s: WalkSection, e: WalkExercise): SetColumn[] {
  return setColumns(e.fields, showsLoad(e.weight, e.fields, s.sectionType), setIsTimed(e.hold ?? e.time),
    e.repsLow !== null || e.repsHigh !== null || e.perReps.length > 0, e.perDist.length > 0, e.perRounds.length > 0);
}
/** What set `i` writes — only the columns its table drew (`LoadFieldPolicy.loggedValues`). */
function loggedOf(st: WalkState, s: WalkSection, e: WalkExercise, k: string, i: number): Logged {
  const v = loggedValues(columnsOf(s, e), {
    load: st.load[k]?.[i] ?? null, reps: st.reps[k]?.[i] ?? null, hold: st.secs[k]?.[i] ?? null,
    typed: st.time[k]?.[i] ?? null, dist: st.dist[k]?.[i] ?? null, rounds: st.rounds[k]?.[i] ?? null,
  });
  return { reps: v.reps, load: v.weightKg, secs: v.holdSeconds, dist: v.distanceM, rounds: v.rounds };
}

/** `vm.unlogSet` + `SetLogUnmark.statusAfterRemoval`. */
function untick(st: WalkState, e: WalkExercise, i: number) {
  if (st.logs[e.id] !== undefined) delete st.logs[e.id]![i];
  const remaining = Object.keys(st.logs[e.id] ?? {}).length;
  if (st.completed.includes(e.id) && remaining < Math.max(e.sets, 1)) st.completed = st.completed.filter((x) => x !== e.id);
}

/** A tick on an exercise / rungs page (`StandaloneExercisePage.confirmSet`). R1: any unlogged set; R2: one un-tick. */
function tickExercise(w: Walk, st: WalkState, p: Page & { exercise: number }, i: number) {
  const s = w.model.sections[p.section]!, e = ex(w, p.section, p.exercise), k = `${p.key}|${e.id}`, n = pageSetCount(w, p)!;
  if (pageCompleted(w, st, p).has(i)) { untick(st, e, i); st.live[p.key] = i; return; }
  const cols = columnsOf(s, e);
  const logged = loggedOf(st, s, e, k, i);
  (st.logs[e.id] ??= {})[i] = logged;
  const done = pageCompleted(w, st, p);
  st.live[p.key] = liveSet(done, n);
  const kinds = Array.from({ length: n }, (_, j) => setKind(e, j));
  for (const j of carryTargets(i, kinds, done)) {
    if (logged.reps !== null && j < st.reps[k]!.length) st.reps[k]![j] = logged.reps;
    if (logged.secs !== null && cols.includes("hold") && j < st.secs[k]!.length) st.secs[k]![j] = logged.secs;
    if (logged.secs !== null && cols.includes("typedTime") && j < st.time[k]!.length) st.time[k]![j] = logged.secs;
    if (logged.load !== null && j < st.load[k]!.length) st.load[k]![j] = logged.load;
    if ((logged.dist ?? null) !== null && j < st.dist[k]!.length) st.dist[k]![j] = logged.dist!;
    if ((logged.rounds ?? null) !== null && j < st.rounds[k]!.length) st.rounds[k]![j] = logged.rounds!;
  }
  if (p.kind === "exercise" && done.size >= n && !st.completed.includes(e.id)) st.completed.push(e.id);
}

/** A tick on a round page (`SupersetPage.confirmSet`): the forward point moves to the slot ticked; an un-tick falls back to
 *  the furthest slot still logged. */
function tickRound(w: Walk, st: WalkState, p: Page & { kind: "round" }, slot: Slot) {
  const s = w.model.sections[p.section]!, e = ex(w, p.section, p.members[slot.member]!), k = `${p.groupKey}|${e.id}`;
  const rounds = groupPlan(w, p.section, p.members);
  const isL = (x: Slot) => isLoggedIn(st, ex(w, p.section, p.members[x.member]!).id, x.setIndex);
  if (isL(slot)) {
    untick(st, e, slot.setIndex);
    let last: Slot | null = null;
    for (const x of rounds.flatMap((r) => r.slots)) if (isL(x)) last = x;
    st.lastLogged[p.groupKey] = last;
    return;
  }
  st.lastLogged[p.groupKey] = slot;
  const hold = setIsTimed(e.hold), load = showsLoad(e.weight, e.fields, s.sectionType), i = slot.setIndex;
  const logged = loggedOf(st, s, e, k, i);
  (st.logs[e.id] ??= {})[i] = logged;
  const done = new Set([...loggedSet(st, e.id)].filter((j) => rounds.some((r) => r.slots.some((x) => x.member === slot.member && x.setIndex === j))));
  const kinds = Array.from({ length: Math.max(e.sets, 1) }, (_, j) => setKind(e, j));
  for (const j of carryTargets(i, kinds, done)) {
    if (!hold && st.reps[k]![i] !== undefined && j < st.reps[k]!.length) st.reps[k]![j] = st.reps[k]![i]!;
    if (hold && st.secs[k]![i] !== undefined && j < st.secs[k]!.length) st.secs[k]![j] = st.secs[k]![i]!;
    const wv = st.load[k]![i];
    if (load && wv !== undefined && wv !== null && j < st.load[k]!.length) st.load[k]![j] = wv;
    if ((logged.dist ?? null) !== null && j < st.dist[k]!.length) st.dist[k]![j] = logged.dist!;
    if ((logged.rounds ?? null) !== null && j < st.rounds[k]!.length) st.rounds[k]![j] = logged.rounds!;
    if (!hold && logged.secs !== null && j < st.time[k]!.length) st.time[k]![j] = logged.secs;
  }
  if (done.size >= e.sets && !st.completed.includes(e.id)) st.completed.push(e.id);
}

/** The page's own list of sets, in order (what a "tick the k-th set" counts in). */
export function pageSets(w: Walk, p: Page): { exercise: number; set: number; slot: Slot | null }[] {
  if (p.kind === "exercise") return Array.from({ length: Math.max(ex(w, p.section, p.exercise).sets, 1) }, (_, i) => ({ exercise: p.exercise, set: i, slot: null }));
  if (p.kind === "rungs") return Array.from({ length: Math.max(p.count, 1) }, (_, i) => ({ exercise: p.exercise, set: i, slot: null }));
  if (p.kind === "round") return p.slots.map((s) => ({ exercise: p.members[s.member]!, set: s.setIndex, slot: s }));
  return [];
}

/** Tick / un-tick one set of the page on screen. */
export function tick(w: Walk, st0: WalkState, exerciseIndex: number, set: number): WalkState {
  const p = w.pages[st0.page];
  if (p === undefined) return st0;
  const st = clone(st0);
  if (p.kind === "round") {
    const m = p.members.indexOf(exerciseIndex);
    if (m < 0) return st0;
    tickRound(w, st, p, { member: m, setIndex: set });
  } else if (p.kind === "exercise" || p.kind === "rungs") {
    if (p.exercise !== exerciseIndex) return st0;
    tickExercise(w, st, p, set);
  } else return st0;
  return st;
}

/** The page's `Mark all` / `Unmark all`. */
export function markAll(w: Walk, st0: WalkState): WalkState {
  const p = w.pages[st0.page];
  if (p === undefined || p.kind === "timed") return st0;
  const st = clone(st0);
  if (p.kind === "round") {
    const rounds = groupPlan(w, p.section, p.members);
    const r = rounds[p.round];
    if (r === undefined) return st0;
    const un = r.slots.filter((x) => !isLoggedIn(st, ex(w, p.section, p.members[x.member]!).id, x.setIndex));
    for (const x of un) tickRound(w, st, p, x);
    return st;
  }
  const decision = markAllPlan(pageSetCount(w, p)!, pageCompleted(w, st, p));
  if (decision.unmark) { for (const i of decision.sets) untick(st, ex(w, p.section, p.exercise), i); st.live[p.key] = 0; }
  else for (const i of decision.sets) tickExercise(w, st, p, i);
  return st;
}

/** `Next exercise ›` / `Finish ›`. */
export function next(w: Walk, st: WalkState): WalkState {
  if (st.page >= w.pages.length) return st;
  return { ...st, page: nextExercisePage(w.pages.map((p) => p.key), st.page) };
}
/** The pager landing on a page (a swipe, a dot). Clamped to 0…END. */
export function goTo(w: Walk, st: WalkState, page: number): WalkState {
  return { ...st, page: Math.min(Math.max(page, 0), w.pages.length) };
}
/** A tap on step `i` of the steps row on screen: a warm-up / cool-down box opens that exercise's first page; a superset's
 *  round box opens that round's page. Opening changes nothing in it. */
export function step(w: Walk, st: WalkState, i: number): WalkState {
  const p = w.pages[st.page];
  if (p === undefined || p.kind === "timed") return st;
  const s = w.model.sections[p.section]!;
  if (s.role === "warm_up" || s.role === "cool_down") {
    if (i < 0 || i >= s.exercises.length) return st;
    const target = w.pages.findIndex((q) => q.section === p.section && (
      q.kind === "exercise" || q.kind === "rungs" ? q.exercise === i : q.kind === "round" ? q.members.includes(i) : true));
    return target < 0 ? st : { ...st, page: target };
  }
  if (p.kind === "round" && i < p.roundCount) return goTo(w, st, st.page + (i - p.round));
  return st;
}

// ─────────────────────────────────────────── what the screen shows ────────────────────────────────────────────

/** W136 B2 S3r5 P5 — `cells`: one text per VALUE column (`columns` less SET and REST), as the phone's cell shows it. */
export type TableRow = { set: number; label: string; cells: string[]; rest: string; logged: boolean; live: boolean };
export type Table = { exercise: string; name: string; note: string; tempo: string; columns: string[]; rows: TableRow[] };
export type Screen = {
  page: number; end: boolean;
  head: { name: string; tail: string; headTitle: string | null; spoken: string };
  rail: { entries: RailKind[]; sections: number[]; current: number | null } & RailLayout & { segments: Segment[]; curves: RailCurve[] };
  steps: { kind: "warmUp" | "coolDown"; title: string | null; labels: string[]; fills: Fill[]; current: number; slot: number; gap: number; scrolls: boolean } | null;
  capsule: string | null; capsuleTarget: number | null;
  tables: Table[];
  markAll: string | null;
  roundSteps: { fills: Fill[]; labels: string[]; title: string | null; current: number } | null;
  timed: { name: string; format: string } | null;
  endPage: { done: number; count: number; canEnd: boolean } | null;
};

function standsFor(w: Walk, st: WalkState, p: Page): number {
  if (p.kind === "exercise" || p.kind === "rungs") return p.exercise;
  if (p.kind === "round") {
    const m = memberOnScreen(p.members.map((x) => ex(w, p.section, x).sets), p.slots,
      (s) => s.member >= 0 && s.member < p.members.length && isLoggedIn(st, ex(w, p.section, p.members[s.member]!).id, s.setIndex));
    return p.members[m] ?? p.members[0] ?? 0;
  }
  return 0;
}

function table(s: WalkSection, e: WalkExercise, rows: TableRow[]): Table {
  const columns = ["SET", ...columnsOf(s, e).map((c) => COLUMN_HEADER[c]), "REST"];
  return { exercise: e.id, name: e.name, note: noteOnPage(e.note), tempo: e.tempo, columns, rows };
}
function row(st: WalkState, s: WalkSection, e: WalkExercise, k: string, i: number, label: string, rest: number, logged: boolean, live: boolean): TableRow {
  const cells = columnsOf(s, e).map((c): string => {
    switch (c) {
      case "kg": { const v = st.load[k]?.[i] ?? null; return v === null ? "" : kg(v); }
      // A set to failure shows its floor as `6+` until it is logged (`SetLogTable`, his 5 Oct note).
      case "reps": { const v = st.reps[k]?.[i]; return v === undefined ? "" : String(v) + (e.types[i] === "failure" && !logged ? "+" : ""); }
      case "hold": { const v = st.secs[k]?.[i]; return v === undefined ? "" : `${String(v)}s`; }
      case "typedTime": return timeText(st.time[k]?.[i] ?? null);
      case "m": case "km": return distanceText(st.dist[k]?.[i] ?? null, c);
      case "rounds": { const v = st.rounds[k]?.[i] ?? null; return v === null ? "" : String(v); }
    }
  });
  return { set: i, label, cells, rest: restText(rest), logged, live };
}

/** Everything the page on screen draws, from the state. `contentWidth` is the phone's content width in points. */
export function screen(w: Walk, st: WalkState, contentWidth: number): Screen {
  const end = w.pages.length, isEnd = st.page >= end;
  const p = isEnd ? undefined : w.pages[st.page];
  const sec = p === undefined ? undefined : w.model.sections[p.section];
  const sectionIdx = p?.section ?? w.model.sections.length;
  const posIndex = p === undefined ? 0 : standsFor(w, st, p);
  const posCount = sec?.exercises.length ?? 0;
  const entries = railEntries(w.model);
  const current = p === undefined ? null : entryIndex(entries, p.section, posIndex);
  const layout = railLayout(entries, contentWidth, current);
  const name = isEnd ? "Finish" : sec?.implicit === true ? w.model.workoutName : sec?.name ?? "";

  let steps: Screen["steps"] = null;
  if (p !== undefined && sec !== undefined && p.kind !== "timed" && (sec.role === "warm_up" || sec.role === "cool_down")) {
    const kind = sec.role === "warm_up" ? "warmUp" : "coolDown";
    const fit = stepsLayout(sec.exercises.length, contentWidth, STEPS.maxSlot);
    steps = {
      kind, title: titleOverSteps(kind), labels: sec.exercises.map((_, i) => stepLabel(kind, i)),
      fills: sec.exercises.map((x) => exerciseFill(x.sets, new Set([...loggedSet(st, x.id)].map((n) => n + 1)))),
      current: posIndex, ...fit,
    };
  }

  const nextPage = p === undefined ? null : nextExercisePage(w.pages.map((q) => q.key), st.page);
  const tables: Table[] = [];
  let markAllTitle: string | null = null;
  let roundSteps: Screen["roundSteps"] = null;
  let timed: Screen["timed"] = null;
  if (p !== undefined && sec !== undefined) {
    if (p.kind === "exercise" || p.kind === "rungs") {
      const e = ex(w, p.section, p.exercise), n = pageSetCount(w, p)!, k = `${p.key}|${e.id}`;
      const done = pageCompleted(w, st, p);
      const live = st.live[p.key] ?? liveSet(done, n);
      const kinds = Array.from({ length: n }, (_, i) => setKind(e, i));
      tables.push(table(sec, e, Array.from({ length: n }, (_, i) =>
        row(st, sec, e, k, i, setLabel(i, e.types), restAfterSet(i, kinds, e.rest), done.has(i), i === live))));
      markAllTitle = markAllPlan(n, done).unmark ? "Unmark all" : "Mark all";
    } else if (p.kind === "round") {
      const rounds = groupPlan(w, p.section, p.members);
      const isL = (s: Slot) => s.member >= 0 && s.member < p.members.length && isLoggedIn(st, ex(w, p.section, p.members[s.member]!).id, s.setIndex);
      const focus = focusSlot(p.slots, rounds, st.lastLogged[p.groupKey] ?? null, isL);
      for (const slot of p.slots) {
        const e = ex(w, p.section, p.members[slot.member]!);
        const rest = restAfter(slot, rounds, p.members.map((m) => ex(w, p.section, m).rest));
        tables.push(table(sec, e, [row(st, sec, e, `${p.groupKey}|${e.id}`, slot.setIndex, setLabel(slot.setIndex, e.types), rest, isL(slot), sameSlot(focus, slot))]));
      }
      const r = rounds[p.round];
      if (r !== undefined && r.slots.some((s) => !isL(s))) markAllTitle = "Mark all";
      roundSteps = { fills: rounds.map((x) => fill(x, isL)), labels: rounds.map((_, i) => stepLabel("rounds", i)), title: titleOverSteps("rounds"), current: p.round };
    } else timed = { name: sec.name, format: sec.format };
  }

  let endPage: Screen["endPage"] = null;
  if (isEnd) {
    const resolved = (x: WalkExercise) => Object.keys(st.logs[x.id] ?? {}).length > 0 || st.completed.includes(x.id);
    const gate = w.model.sections.map((s) => ({
      main: !["warm_up", "cool_down", "mobility", "recovery"].includes(s.role), count: s.exercises.length,
      finished: s.exercises.filter(resolved).length,
    }));
    const main = gate.filter((g) => g.main);
    const canEnd = gate.length > 0 && !(main.length > 0 && main.every((g) => g.finished === 0));
    endPage = { done: gate.reduce((a, g) => a + g.finished, 0), count: gate.reduce((a, g) => a + g.count, 0), canEnd };
  }

  return {
    page: st.page, end: isEnd,
    head: {
      name, tail: positionTail(isEnd, isEnd ? 0 : posIndex, isEnd ? 1 : posCount),
      headTitle: headTitle(sec?.role ?? null, isEnd),
      spoken: spokenHead(isEnd, sectionIdx, w.model.sections.length, isEnd ? 0 : posIndex, isEnd ? 1 : posCount),
    },
    rail: { entries: entries.map((e) => e.kind), sections: entries.map((e) => e.section), current, ...layout, segments: segments(entries, layout.xs, current), curves: railCurves(entries, layout.xs) },
    steps,
    capsule: nextPage === null ? null : nextPage >= end ? "Finish" : "Next exercise",
    capsuleTarget: nextPage,
    tables, markAll: markAllTitle, roundSteps, timed, endPage,
  };
}

// ─────────────────────────────────────────── the finish page (W133) ────────────────────────────────────────────

export type Mark = "done" | "partial" | "skipped" | "notLogged";
export type FinishFacts = { prescribedSets: number; workingLogged: number; warmupLogged: number; markedSkipped: boolean; markedCompleted: boolean };
/** `W133FinishPage.mark`. */
export function finishMark(f: FinishFacts): Mark {
  if (f.markedSkipped && f.workingLogged === 0) return "skipped";
  if (f.workingLogged > 0) return f.workingLogged >= f.prescribedSets ? "done" : "partial";
  if (f.markedCompleted) return "done";
  if (f.warmupLogged > 0) return "partial";
  return "notLogged";
}
/** `W133FinishPage.summary`. */
export function finishSummary(all: readonly FinishFacts[]) {
  return {
    setsLogged: all.reduce((a, f) => a + Math.min(Math.max(f.workingLogged, 0), Math.max(f.prescribedSets, 0)), 0),
    setsPrescribed: all.reduce((a, f) => a + Math.max(f.prescribedSets, 0), 0),
    exercisesDone: all.filter((f) => finishMark(f) === "done").length,
    exercisesTotal: all.length,
  };
}
const UNNAMED = ["", "Superset", "Main Workout", "Loose work"];
/** `W133FinishPage.sectionLabel`. */
export function sectionLabel(name: string, role: string, isImplicit: boolean): string {
  const trimmed = name.trim();
  if (!isImplicit && !UNNAMED.includes(trimmed)) return trimmed;
  if (role === "warm_up" || role === "activation") return "Warm-up";
  if (role === "cool_down" || role === "mobility" || role === "recovery") return "Cool-down";
  return "Main work";
}

export type Finish = {
  sections: { label: string; rows: { exercise: string; name: string; mark: Mark; line: string }[] }[];
  summary: ReturnType<typeof finishSummary>;
};
/** The finish page from the store (`resultModel`): `SetLogKind.status` decides what counts as working — a set in a warm-up
 *  SECTION or typed warm-up is logged `warmup`. */
export function finish(w: Walk, st: WalkState): Finish {
  const all: FinishFacts[] = [];
  const sections = w.model.sections.map((s) => ({
    label: sectionLabel(s.name, s.role, s.implicit),
    rows: s.exercises.map((e) => {
      const logs = st.logs[e.id] ?? {};
      const idx = Object.keys(logs).map(Number).sort((a, b) => a - b);
      const isWarm = (i: number) => s.role === "warm_up" || setKind(e, i) === "warm_up";
      const working = idx.filter((i) => !isWarm(i));
      const rungs = idx.filter(isWarm).length;
      const facts: FinishFacts = { prescribedSets: e.sets, workingLogged: working.length, warmupLogged: rungs, markedSkipped: false, markedCompleted: st.completed.includes(e.id) };
      all.push(facts);
      const mark = finishMark(facts);
      let line: string;
      if (working.length > 0) {
        const sets = readoutLine(working.map((i) => ({ setNumber: i + 1, reps: logs[i]!.reps, load: logs[i]!.load, secs: logs[i]!.secs,
                                                       dist: logs[i]!.dist ?? null, rounds: logs[i]!.rounds ?? null })),
                                 e.fields.includes("distance_km"));
        line = rungs > 0 ? `${sets}  ·  ${String(rungs)} warm-up set${rungs === 1 ? "" : "s"}` : sets;
      } else line = mark === "done" ? "Checked off" : mark === "partial" ? "Warm-up sets only" : "Not logged";
      return { exercise: e.id, name: e.name, mark, line };
    }),
  }));
  return { sections, summary: finishSummary(all) };
}
